'use client';

import { useApi } from '@/hooks/useApi';
import { UseFormActions } from '@/hooks/useForm';

import { sendGAEvent } from '@/integrations/trackingScripts';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { apiEndpoints } from '@/constant/urls';

import {
  ApplicationForm,
  ApplicationFormSchema,
} from './components/ApplicationForm';

export type JobApplicationFormProps = {
  job: {
    role: string;
    hrEmail: string;
  };
};

export function JobApplicationForm({ job }: JobApplicationFormProps) {
  const {
    loading: isSubmitting,
    makeApiCall: submit,
    error: submitError,
    data: submitData,
  } = useApi({
    url: apiEndpoints.jobApplication,
    method: 'POST',
  });

  async function handleSubmit(
    data: ApplicationFormSchema,
    action: UseFormActions
  ) {
    const formData = new FormData();
    formData.set('role', job.role);
    formData.set('hr_email', job.hrEmail);
    formData.set('full_name', data.fullName);
    formData.set('email', data.email);
    formData.set('phone', data.countryCode + data.phoneNumber);
    if (data.linkedInProfile) {
      formData.set('profile_link', data.linkedInProfile);
    }
    if (data.information) {
      formData.set('info', data.information);
    }
    if (data.resume) {
      formData.set('file', data.resume);
    }

    try {
      const response = await submit({
        body: formData,
      });
      if (response?.error) return;
      sendGAEvent({
        event: 'ApplicationForm_submitted',
      });
      action.resetForm();
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <BlockWrapper
      data-tracker-section={SECTION_NAMES.JOB_APPLICATION_FORM}
      classNames={{ root: 'bg-gray-25' }}
    >
      <BlockHeaderContent title='Submit your application' alignment='left'>
        <div className='max-w-[584px]'>
          <ApplicationForm
            onSubmit={handleSubmit}
            loading={isSubmitting}
            data={submitData}
            error={submitError ? Error(submitError) : undefined}
          />
        </div>
      </BlockHeaderContent>
    </BlockWrapper>
  );
}
